import { useState, useEffect, useCallback } from 'react';

interface ApprovalRequest {
  approvalId: string;
  loopId: string;
  tool: string;
  input: Record<string, any>;
}

interface ToolApprovalState {
  pending: ApprovalRequest | null;
  approve: () => void;
  approveAlways: () => void;
  deny: () => void;
}

const isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI?.isElectron;

/**
 * Hook that listens for tool approval requests from the Electron main process
 * and exposes state for rendering a confirmation modal.
 *
 * DEV MODE: Press Ctrl+Shift+T to trigger a test approval popup.
 */
export function useToolApproval(): ToolApprovalState {
  const [pending, setPending] = useState<ApprovalRequest | null>(null);
  const [alwaysAllowed, setAlwaysAllowed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;
    electronAPI.onToolApprovalRequest?.((request: ApprovalRequest) => {
      // Auto-approve if the user previously chose "Always Allow" for this tool type
      if (alwaysAllowed.has(request.tool)) {
        electronAPI.respondToolApproval(request.approvalId, true);
        return;
      }
      setPending(request);
    });

    return () => {
      electronAPI.removeToolApprovalListener?.();
    };
  }, [alwaysAllowed]);

  // DEV: Keyboard shortcut to test approval UI (Ctrl+Shift+T)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        // Cycle through different test cases
        const testCases = [
          {
            approvalId: 'test-bash',
            loopId: 'test-loop',
            tool: 'bash',
            input: { command: 'npm run build --dangerously-skip-permissions' }
          },
          {
            approvalId: 'test-edit',
            loopId: 'test-loop',
            tool: 'edit_file',
            input: {
              path: 'src/components/ChatBubble.jsx',
              old_str: 'const ChatBubble = () => {\n  return <div>Old</div>;\n}',
              new_str: 'const ChatBubble = () => {\n  return <div>New</div>;\n}'
            }
          },
          {
            approvalId: 'test-create',
            loopId: 'test-loop',
            tool: 'create_file',
            input: {
              path: 'src/utils/newHelper.ts',
              content: 'export function helper() {\n  return "test";\n}\n'
            }
          }
        ];

        const randomCase = testCases[Math.floor(Math.random() * testCases.length)];
        setPending(randomCase);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const respond = useCallback((approved: boolean) => {
    if (!pending) return;
    if (isElectron) {
      (window as any).electronAPI.respondToolApproval(pending.approvalId, approved);
    } else {
      // DEV: Just log for testing
      console.log(`Tool approval ${approved ? 'approved' : 'denied'}:`, pending);
    }
    setPending(null);
  }, [pending]);

  const approve = useCallback(() => respond(true), [respond]);
  const deny = useCallback(() => respond(false), [respond]);
  const approveAlways = useCallback(() => {
    if (!pending) return;
    setAlwaysAllowed(prev => new Set(prev).add(pending.tool));
    respond(true);
  }, [pending, respond]);

  return { pending, approve, approveAlways, deny };
}

/**
 * Format a tool approval request into a human-readable title + description.
 * Keeps the modal clean by extracting command type rather than dumping raw content.
 */
export function formatToolApproval(tool: string, input: Record<string, any>): { title: string; message: string } {
  switch (tool) {
    case 'bash':
      return {
        title: summarizeBashCommand(input.command || ''),
        message: truncateLines(input.command || 'Unknown command', 4),
      };
    case 'edit_file':
      return {
        title: `Edit file: ${input.path || 'unknown'}`,
        message: `${countLines(input.old_str)} line(s) → ${countLines(input.new_str)} line(s)`,
      };
    case 'create_file': {
      const size = input.content?.length || 0;
      return {
        title: `Create file: ${input.path || 'unknown'}`,
        message: `${formatBytes(size)}, ${countLines(input.content)} lines`,
      };
    }
    default:
      return {
        title: `Execute ${tool}`,
        message: truncateLines(JSON.stringify(input, null, 2), 4),
      };
  }
}

/**
 * Extract a short human-readable label from a bash command.
 * e.g. "pip3 install foo && python3 -c '...'" → "Run: pip3 install, python3"
 */
function summarizeBashCommand(command: string): string {
  // Split on && / || / ; / | to get individual commands
  const parts = command.split(/\s*(?:&&|\|\||;|\|)\s*/);
  const labels = parts
    .map(part => {
      const tokens = part.trim().split(/\s+/);
      if (tokens.length === 0) return null;
      // Take the program name + first arg (if it's a subcommand like "pip install")
      const program = tokens[0];
      const subcommand = tokens[1] && !tokens[1].startsWith('-') && !tokens[1].startsWith('"') && !tokens[1].startsWith("'")
        ? ` ${tokens[1]}`
        : '';
      return `${program}${subcommand}`;
    })
    .filter(Boolean);

  if (labels.length === 0) return 'Run shell command';
  if (labels.length === 1) return `Run: ${labels[0]}`;
  if (labels.length <= 3) return `Run: ${labels.join(', ')}`;
  return `Run: ${labels.slice(0, 3).join(', ')} (+${labels.length - 3} more)`;
}

function truncateLines(str: string | undefined, maxLines: number): string {
  if (!str) return '';
  const lines = str.split('\n');
  if (lines.length <= maxLines) return str;
  return lines.slice(0, maxLines).join('\n') + `\n… (${lines.length - maxLines} more lines)`;
}

function countLines(str: string | undefined): number {
  if (!str) return 0;
  return str.split('\n').length;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
